import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { supabase } from '../../lib/supabase';
import { useState, useEffect } from 'react';

interface StockMap {
  [productId: number]: number;
}

export default function CartDrawer() {
  const { items, removeItem, updateQuantity, totalPrice, totalItems, isCartOpen, setIsCartOpen, clearCart } = useCart();
  const navigate = useNavigate();
  const [step, setStep] = useState<'cart' | 'checkout' | 'success'>('cart');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [orderId, setOrderId] = useState<number | null>(null);
  const [stockMap, setStockMap] = useState<StockMap>({});
  const [stockLoading, setStockLoading] = useState(false);

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'ES',
  });

  // Fetch current stock from Supabase whenever cart opens or items change
  useEffect(() => {
    if (!isCartOpen || items.length === 0) return;
    const fetchStock = async () => {
      setStockLoading(true);
      const ids = items.map((i) => i.productId);
      const { data } = await supabase.from('products').select('id, stock').in('id', ids);
      const map: StockMap = {};
      if (data) {
        for (const p of data) {
          map[p.id] = p.stock ?? 9999;
        }
      }
      setStockMap(map);
      setStockLoading(false);
    };
    fetchStock();
  }, [isCartOpen, items]);

  // Close on ESC
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isCartOpen) setIsCartOpen(false);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isCartOpen, setIsCartOpen]);

  const getAvailableStock = (productId: number) => {
    return stockMap[productId] ?? 9999;
  };

  const handleCheckout = async () => {
    if (!form.name || !form.email || !form.address || !form.city || !form.postalCode) {
      setCheckoutError('Completa todos los campos obligatorios');
      return;
    }

    // Final stock validation before sending to Stripe
    for (const item of items) {
      const available = getAvailableStock(item.productId);
      if (item.quantity > available) {
        setCheckoutError(`Stock insuficiente para "${item.name}". Disponible: ${available}`);
        return;
      }
    }

    setCheckoutLoading(true);
    setCheckoutError(null);

    const payload = {
      name: form.name,
      email: form.email,
      phone: form.phone,
      shipping_address: form.address,
      shipping_city: form.city,
      shipping_postal_code: form.postalCode,
      shipping_country: form.country,
      items: items.map((i) => ({
        name: i.name,
        unit_price: i.price,
        quantity: i.quantity,
        image: i.image,
        product_id: i.productId,
      })),
      origin_url: window.location.origin + '/tienda',
    };

    try {
      const { data, error } = await supabase.functions.invoke('stripe-product-checkout', { body: payload });
      if (error || data?.error) {
        setCheckoutError(error?.message || data?.error || 'Error al procesar el pago');
        setCheckoutLoading(false);
        return;
      }
      if (data?.url) {
        setOrderId(data.order_id);
        window.location.href = data.url;
      } else {
        setCheckoutError('No se recibió URL de Stripe');
      }
    } catch (e: any) {
      setCheckoutError(e.message || 'Error de red');
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (!isCartOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-[200] transition-all duration-500 ${
        isCartOpen ? 'pointer-events-auto' : 'pointer-events-none'
      }`}
    >
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-brand-black/40 backdrop-blur-sm transition-opacity duration-500 ${
          isCartOpen ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={() => setIsCartOpen(false)}
      />

      {/* Floating Panel */}
      <div
        className={`absolute top-3 right-3 sm:right-5 lg:right-8 bottom-3 sm:bottom-5 lg:bottom-8 w-full max-w-sm sm:max-w-md bg-white rounded-[32px] shadow-2xl flex flex-col overflow-hidden transition-all duration-500 ${
          isCartOpen ? 'translate-x-0 opacity-100' : 'translate-x-8 opacity-0'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-8 pt-8 pb-4 flex-shrink-0">
          <span className="font-sans text-[11px] tracking-[0.2em] uppercase text-brand-gray-400">
            {step === 'cart' ? `Tu Cesta · ${totalItems} ${totalItems === 1 ? 'artículo' : 'artículos'}` : 'Finalizar Compra'}
          </span>
          <button
            onClick={() => setIsCartOpen(false)}
            className="w-10 h-10 rounded-full bg-brand-gray-100 flex items-center justify-center text-brand-black hover:bg-brand-gray-200 transition-colors"
            aria-label="Cerrar cesta"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-8 pb-8">
          {step === 'cart' && (
            <>
              {items.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-brand-gray-50 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-shopping-bag-line text-brand-gray-400 text-2xl" />
                  </div>
                  <p className="font-sans text-brand-gray-500 text-sm">Tu cesta está vacía</p>
                  <button
                    onClick={() => { setIsCartOpen(false); navigate('/tienda'); }}
                    className="mt-4 font-sans text-brand-black text-sm border-b border-brand-black pb-0.5 hover:text-brand-gray-500 transition-colors"
                  >
                    Ir a la tienda
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  {stockLoading && (
                    <div className="flex items-center gap-2 text-brand-gray-400 text-xs font-sans">
                      <i className="ri-loader-4-line animate-spin" />
                      Verificando stock...
                    </div>
                  )}

                  {items.map((item) => {
                    const available = getAvailableStock(item.productId);
                    const isOverStock = item.quantity > available;

                    return (
                      <div key={item?.productId || index} className="flex gap-4">
                        <div className="w-20 h-20 rounded-2xl overflow-hidden bg-brand-gray-50 flex-shrink-0">
                          <img src={item?.image || ''} alt={item?.name || ''} className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-sans text-brand-black text-sm font-medium truncate">{item?.name || 'Producto'}</p>
                          <p className="font-sans text-brand-gray-400 text-xs capitalize mt-0.5">{item?.category || ''}</p>
                          <p className="font-sans text-brand-gray-400 text-[11px] mt-0.5">
                            Stock disponible: {available}
                          </p>
                          {isOverStock && (
                            <p className="font-sans text-red-500 text-[11px] mt-0.5">
                              Has superado el stock disponible
                            </p>
                          )}
                          <div className="flex items-center justify-between mt-2">
                            <div className="flex items-center gap-2 border border-brand-gray-200 rounded-xl overflow-hidden">
                              <button
                                onClick={() => updateQuantity(item.productId, (item?.quantity || 1) - 1)}
                                className="w-7 h-7 flex items-center justify-center text-brand-gray-500 hover:text-brand-black hover:bg-brand-gray-50 transition-colors"
                              >
                                <i className="ri-subtract-line text-xs" />
                              </button>
                              <span className="font-sans text-brand-black text-xs w-4 text-center">{item?.quantity || 1}</span>
                              <button
                                onClick={() => {
                                  if ((item?.quantity || 1) < available) {
                                    updateQuantity(item.productId, (item?.quantity || 1) + 1);
                                  }
                                }}
                                disabled={(item?.quantity || 1) >= available}
                                className={`w-7 h-7 flex items-center justify-center transition-colors ${
                                  (item?.quantity || 1) >= available
                                    ? 'text-brand-gray-300 cursor-not-allowed'
                                    : 'text-brand-gray-500 hover:text-brand-black hover:bg-brand-gray-50'
                                }`}
                              >
                                <i className="ri-add-line text-xs" />
                              </button>
                            </div>
                            <p className="font-sans text-brand-black text-sm font-semibold">
                              {((item?.price || 0) * (item?.quantity || 1)).toLocaleString('es-ES', {
                                style: 'currency',
                                currency: 'EUR',
                                minimumFractionDigits: 0,
                              })}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => removeItem(item.productId)}
                          className="w-7 h-7 flex items-center justify-center text-brand-gray-400 hover:text-red-500 transition-colors self-start"
                        >
                          <i className="ri-delete-bin-line text-sm" />
                        </button>
                      </div>
                    );
                  })}

                  <div className="rounded-2xl bg-brand-gray-50 p-5 space-y-3">
                    <div className="flex items-center justify-between font-sans text-brand-black text-sm">
                      <span>Subtotal</span>
                      <span className="font-semibold">
                        {(totalPrice || 0).toLocaleString('es-ES', {
                          style: 'currency',
                          currency: 'EUR',
                          minimumFractionDigits: 0,
                        })}
                      </span>
                    </div>
                    <div className="flex items-center justify-between font-sans text-brand-gray-400 text-xs">
                      <span>Envío</span>
                      <span>Calculado al finalizar</span>
                    </div>
                    <div className="flex items-center justify-between font-serif text-brand-black text-lg font-medium pt-2 border-t border-brand-gray-200">
                      <span>Total</span>
                      <span>
                        {(totalPrice || 0).toLocaleString('es-ES', {
                          style: 'currency',
                          currency: 'EUR',
                          minimumFractionDigits: 0,
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {step === 'checkout' && (
            <div className="space-y-4">
              <div>
                <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Nombre completo *</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 w-full border border-brand-gray-200 rounded-xl px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black transition-colors"
                />
              </div>
              <div>
                <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Email *</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1 w-full border border-brand-gray-200 rounded-xl px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black transition-colors"
                />
              </div>
              <div>
                <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Teléfono</label>
                <input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="mt-1 w-full border border-brand-gray-200 rounded-xl px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black transition-colors"
                />
              </div>
              <div>
                <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Dirección *</label>
                <input
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className="mt-1 w-full border border-brand-gray-200 rounded-xl px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black transition-colors"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Ciudad *</label>
                  <input
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    className="mt-1 w-full border border-brand-gray-200 rounded-xl px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black transition-colors"
                  />
                </div>
                <div>
                  <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">CP *</label>
                  <input
                    value={form.postalCode}
                    onChange={(e) => setForm({ ...form, postalCode: e.target.value })}
                    className="mt-1 w-full border border-brand-gray-200 rounded-xl px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">País</label>
                <select
                  value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value })}
                  className="mt-1 w-full border border-brand-gray-200 rounded-xl px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black bg-white transition-colors"
                >
                  <option value="ES">España</option>
                  <option value="FR">Francia</option>
                  <option value="IT">Italia</option>
                  <option value="PT">Portugal</option>
                  <option value="DE">Alemania</option>
                  <option value="BE">Bélgica</option>
                  <option value="NL">Países Bajos</option>
                  <option value="AT">Austria</option>
                  <option value="CH">Suiza</option>
                  <option value="GB">Reino Unido</option>
                </select>
              </div>

              {checkoutError && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-start gap-2">
                  <i className="ri-error-warning-line text-red-500 mt-0.5" />
                  <p className="font-sans text-red-600 text-xs">{checkoutError}</p>
                </div>
              )}
            </div>
          )}

          {step === 'success' && (
            <div className="text-center py-10">
              <div className="w-16 h-16 rounded-full bg-brand-sage flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-brand-black text-2xl" />
              </div>
              <h3 className="font-serif text-brand-black text-xl">¡Pedido realizado!</h3>
              <p className="font-sans text-brand-gray-500 text-sm mt-2">
                Te enviaremos un email con la confirmación.
              </p>
              <p className="font-sans text-brand-gray-400 text-xs mt-1">
                Pedido #{orderId}
              </p>
              <button
                onClick={() => { setIsCartOpen(false); clearCart(); setStep('cart'); }}
                className="mt-6 font-sans text-brand-black text-sm border-b border-brand-black pb-0.5 hover:text-brand-gray-500 transition-colors"
              >
                Seguir comprando
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        {step === 'cart' && items.length > 0 && (
          <div className="px-8 pb-8 pt-4 flex-shrink-0 space-y-3">
            <button
              onClick={() => setStep('checkout')}
              disabled={items.some((i) => i.quantity > getAvailableStock(i.productId))}
              className="w-full bg-brand-black text-white font-sans text-sm tracking-widest uppercase py-4 rounded-full hover:bg-brand-gray-600 transition-all duration-300 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Finalizar compra
            </button>
            <button
              onClick={() => { setIsCartOpen(false); navigate('/tienda'); }}
              className="w-full border border-brand-gray-200 text-brand-black font-sans text-sm py-3 rounded-full hover:border-brand-black transition-all whitespace-nowrap"
            >
              Seguir comprando
            </button>
          </div>
        )}

        {step === 'checkout' && (
          <div className="px-8 pb-8 pt-4 flex-shrink-0 space-y-3">
            <div className="flex items-center justify-between font-serif text-brand-black text-lg font-medium mb-2">
              <span>Total</span>
              <span>
                {totalPrice.toLocaleString('es-ES', {
                  style: 'currency',
                  currency: 'EUR',
                  minimumFractionDigits: 0,
                })}
              </span>
            </div>
            <button
              onClick={handleCheckout}
              disabled={checkoutLoading}
              className="w-full bg-brand-black text-white font-sans text-sm tracking-widest uppercase py-4 rounded-full hover:bg-brand-gray-600 transition-all duration-300 whitespace-nowrap disabled:opacity-60"
            >
              {checkoutLoading ? 'Procesando...' : 'Pagar con Stripe'}
            </button>
            <button
              onClick={() => setStep('cart')}
              className="w-full border border-brand-gray-200 text-brand-black font-sans text-sm py-3 rounded-full hover:border-brand-black transition-all whitespace-nowrap"
            >
              Volver al carrito
            </button>
          </div>
        )}
      </div>
    </div>
  );
}